/**
 * End-of-call "Review participants" — the pure half: which rows the sheet
 * shows and what a Save sends. The macOS and Windows apps implement the same
 * rules, so keep this free of React and Redux.
 *
 * Ids are compared as strings everywhere: recall ids are persisted as JSON
 * numbers while live events carry strings (see the roster reducers).
 */
import { ParticipantRole } from '../config/participants';

export interface ReviewRosterEntry {
  participantId?: string | number;
  name?: string;
  role?: string;
}

export interface ReviewTranscript {
  participantId?: string | number;
  speaker?: string;
  speakerName?: string;
  text?: string;
}

export interface ReviewRow {
  participantId: string;
  name: string;
  role: ParticipantRole;
  turnCount: number;
  /** The participant's first transcript line (untruncated), shown under the name so the rep can tell voices apart. */
  firstLine?: string;
}

export interface RowEdit {
  name: string;
  role: ParticipantRole;
}

/** One entry of the PUT /participants body — only the fields that changed. */
export interface ParticipantEdit {
  participantId: string;
  name?: string;
  role?: ParticipantRole;
}

/**
 * First lines longer than this are cut and end with an ellipsis. Fixed in
 * characters (not pixels) so the web and both desktop apps truncate identically.
 */
export const MAX_QUOTE_LENGTH = 80;

/** Cut a first line to MAX_QUOTE_LENGTH: the first 79 characters, trailing spaces trimmed, then "…". */
export const truncateQuote = (text: string): string => {
  const characters = Array.from(text);
  if (characters.length <= MAX_QUOTE_LENGTH) return text;
  return `${characters.slice(0, MAX_QUOTE_LENGTH - 1).join('').trimEnd()}\u2026`;
};

/** The creator alone is one speaker; a second speaker means someone else talked. */
const MIN_SPEAKERS_TO_REVIEW = 2;

const toId = (rawId: string | number | undefined): string =>
  rawId === undefined || rawId === null ? '' : String(rawId).trim();

const toRole = (rawRole: string | undefined): ParticipantRole | null => {
  if (rawRole === ParticipantRole.SALESPERSON) return ParticipantRole.SALESPERSON;
  if (rawRole === ParticipantRole.CUSTOMER) return ParticipantRole.CUSTOMER;
  return null;
};

interface SpeakerTally {
  turnCount: number;
  lastSpeakerName?: string;
  lastSpeakerRole: ParticipantRole | null;
  firstLine?: string;
}

const tallySpeakers = (transcripts: ReviewTranscript[]): Map<string, SpeakerTally> => {
  const tallies = new Map<string, SpeakerTally>();
  for (const transcript of transcripts) {
    const participantId = toId(transcript?.participantId);
    if (!participantId) continue;
    const tally = tallies.get(participantId) || { turnCount: 0, lastSpeakerRole: null };
    tally.turnCount += 1;
    tally.lastSpeakerName = transcript?.speakerName || tally.lastSpeakerName;
    tally.lastSpeakerRole = toRole(transcript?.speaker) || tally.lastSpeakerRole;
    tally.firstLine = tally.firstLine || (transcript?.text || '').trim() || undefined;
    tallies.set(participantId, tally);
  }
  return tallies;
};

const compareRows = (left: ReviewRow, right: ReviewRow): number => {
  if (left.role !== right.role) return left.role === ParticipantRole.SALESPERSON ? -1 : 1;
  return right.turnCount - left.turnCount;
};

/**
 * One row per participant who spoke (a transcript carrying a participantId).
 * Name and role come from the roster, falling back to the participant's own
 * transcripts. Salespeople first, then customers, each by turn count descending.
 */
export const buildReviewRows = (
  roster: ReviewRosterEntry[],
  transcripts: ReviewTranscript[],
): ReviewRow[] => {
  const rosterById = new Map((roster || []).map((entry) => [toId(entry?.participantId), entry]));
  const rows: ReviewRow[] = [];
  tallySpeakers(transcripts || []).forEach((tally, participantId) => {
    const rosterEntry = rosterById.get(participantId);
    rows.push({
      participantId,
      name: rosterEntry?.name || tally.lastSpeakerName || '',
      role: toRole(rosterEntry?.role) || tally.lastSpeakerRole || ParticipantRole.CUSTOMER,
      turnCount: tally.turnCount,
      ...(tally.firstLine ? { firstLine: tally.firstLine } : {}),
    });
  });
  return rows.sort(compareRows);
};

/** The sheet is worth showing only when someone besides the creator spoke. */
export const shouldReviewParticipants = (rows: ReviewRow[]): boolean =>
  rows.length >= MIN_SPEAKERS_TO_REVIEW;

export const initialEdits = (rows: ReviewRow[]): Record<string, RowEdit> =>
  Object.fromEntries(rows.map((row) => [row.participantId, { name: row.name, role: row.role }]));

const editFor = (row: ReviewRow, edits: Record<string, RowEdit>): RowEdit =>
  edits[row.participantId] || { name: row.name, role: row.role };

/** Fallback label for a speaker with no name anywhere. */
export const unnamedParticipantLabel = (participantId: string): string => `Participant ${participantId}`;

/**
 * Only what changed: `name` when the trimmed name is non-empty and differs,
 * `role` when it differs. Unchanged rows are omitted, so an empty result means
 * "nothing to save".
 */
export const diffParticipantEdits = (
  rows: ReviewRow[],
  edits: Record<string, RowEdit>,
): ParticipantEdit[] => {
  const changes: ParticipantEdit[] = [];
  for (const row of rows) {
    const edit = editFor(row, edits);
    const trimmedName = (edit.name || '').trim();
    const change: ParticipantEdit = { participantId: row.participantId };
    if (trimmedName && trimmedName !== row.name) change.name = trimmedName;
    if (edit.role !== row.role) change.role = edit.role;
    if (change.name !== undefined || change.role !== undefined) changes.push(change);
  }
  return changes;
};

export const hasRoleChange = (rows: ReviewRow[], edits: Record<string, RowEdit>): boolean =>
  rows.some((row) => editFor(row, edits).role !== row.role);

/**
 * The server refuses an edit that leaves the call without a salesperson. A
 * salesperson on the roster who never spoke has no row but still counts, the
 * same as on the server.
 */
export const hasSalesperson = (
  rows: ReviewRow[],
  edits: Record<string, RowEdit>,
  roster: ReviewRosterEntry[] = [],
): boolean => {
  if (rows.some((row) => editFor(row, edits).role === ParticipantRole.SALESPERSON)) return true;
  const rowIds = new Set(rows.map((row) => row.participantId));
  return roster.some(
    (entry) =>
      toRole(entry?.role) === ParticipantRole.SALESPERSON && !rowIds.has(toId(entry?.participantId)),
  );
};
